import { useMemo } from "react";

import { useDndContext } from "@dnd-kit/core";

import { EMPTY_ARRAY } from "app/pages/sales-intelligence/constants/common";
import type { ScoringConfigRule } from "app/pages/sales-intelligence/domain/leads/types/scoring-config";
import type { LeadPriorityScore } from "app/pages/sales-intelligence/modules/lists/types/columns";

import type { ScoringBoardSharedProps } from "./scoringBoardTypes";
import { ScoringRuleCard } from "./ScoringRuleCard";
import { StyledDragOverlayCard } from "./styles";

type ActiveDragState = {
    ruleId: string;
    width: number;
    sourcePriority: LeadPriorityScore;
    ruleIndex: number;
};

type Props = Pick<
    ScoringBoardSharedProps,
    "filtersList" | "signalById" | "getRuleScope" | "scopeBadgeKeys" | "getRuleUsage"
> & {
    rules: ScoringConfigRule;
    activeDrag: ActiveDragState | null;
};

export const ScoringBoardDragOverlay = ({
    rules,
    activeDrag,
    filtersList,
    signalById,
    getRuleScope,
    scopeBadgeKeys,
    getRuleUsage,
}: Props) => {
    const { active } = useDndContext();

    const activeRule = useMemo(() => {
        if (!activeDrag) {
            return null;
        }

        return rules[activeDrag.sourcePriority]?.[activeDrag.ruleIndex] ?? null;
    }, [activeDrag, rules]);

    if (!active || !activeRule || !activeDrag) {
        return null;
    }

    return (
        <StyledDragOverlayCard
            $width={activeDrag.width}
            data-automation="scoring-board.drag-overlay"
        >
            <ScoringRuleCard
                rule={activeRule}
                filtersList={filtersList}
                signalById={signalById}
                scope={getRuleScope?.(activeRule)}
                scopeBadgeKeys={scopeBadgeKeys}
                usedByPlaybookNames={getRuleUsage?.(activeRule) ?? EMPTY_ARRAY}
            />
        </StyledDragOverlayCard>
    );
};

export type { ActiveDragState };
